import { Component, OnInit, inject } from '@angular/core';
import { HubRecordEditorBase } from '../../_hub_record_editor_base';
import { ProjectInfoModel, ProjectRelationModel } from '../../../../../hub_schema/hubTypes';
import { FormControl } from '@angular/forms';
import { Observable, debounceTime, distinctUntilChanged, switchMap } from 'rxjs';
import { RelationsTypeaheadService } from '../services/relations-typeahead.service';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

@Component({
    selector: 'app-program-management-relations-edit',
    templateUrl: './program-management-relations-edit.component.html',
    styleUrls: ['./program-management-relations-edit.component.scss'],
    standalone: false
})
export class ProgramManagementRelationsEditComponent extends HubRecordEditorBase implements OnInit {
    private relationsTypeaheadService: RelationsTypeaheadService = inject(RelationsTypeaheadService);
    
    public relatedStrategies: ProjectInfoModel[];
    public relatedProjects: ProjectInfoModel[];
    public relatedWholeSystems: ProjectInfoModel[];

    public strategyTypeahead: FormControl = new FormControl('');
    public strategySuggestions$: Observable<ProjectInfoModel[]>;

    public projectTypeahead: FormControl = new FormControl('');
    public projectsSuggestions$: Observable<ProjectInfoModel[]>;

    public wholeSystemTypeahead: FormControl = new FormControl('');
    public wholeSystemSuggestions$: Observable<ProjectInfoModel[]>;

    public ngOnInit(): void {
        const relations = this.projectEditForm?.controls.relations.value as ProjectRelationModel[];
        this.relatedStrategies = relations.filter(r => r.relatedProject?.projectTypeName === 'Strategy').map(r => r.relatedProject!);
        this.relatedProjects = relations.filter(r => r.relatedProject?.projectTypeName === 'Project').map(r => r.relatedProject!);
        this.relatedWholeSystems = relations.filter(r => r.relatedProject?.projectTypeName === 'Whole System').map(r => r.relatedProject!);

        // wireup typeaheads
        this.strategySuggestions$ = this.strategyTypeahead.valueChanges
        .pipe(
            debounceTime(200),
            distinctUntilChanged(),
            switchMap((term: string) =>
                this.relationsTypeaheadService.getStrategiesByNamePrefix(term))
        );

        this.projectsSuggestions$ = this.projectTypeahead.valueChanges
        .pipe(
            debounceTime(200),
            distinctUntilChanged(),
            switchMap((term: string) =>
                this.relationsTypeaheadService.getProjectsByNamePrefix(term))
        );

        this.wholeSystemSuggestions$ = this.wholeSystemTypeahead.valueChanges
            .pipe(
                debounceTime(200),
                distinctUntilChanged(),
                switchMap((term: string) =>
                    this.relationsTypeaheadService.getWholeSystemsByNamePrefix(term))    
            );
    }

    // #region Related Strategies

    public isStrategyDisabled(strategy: ProjectInfoModel): boolean {
        const existingStrategyIds = this.projectEditForm?.controls.relations.value.map(s => s.relatedProjectId);
        return existingStrategyIds.includes(strategy.projectId) || this.isRelationDisabledDueToStatus(strategy);
    }

    public addRelatedStrategy(e: MatAutocompleteSelectedEvent) {
        const project = e.option.value as ProjectInfoModel;

        const newRelation = {
            projectId: this.project.projectId,
            relatedProjectId: project.projectId,            
        };

        this.relatedStrategies.push(project);
        this.projectEditForm?.controls.relations.value.push(newRelation);

        this.projectEditForm?.controls.relations.markAsDirty();
        this.strategyTypeahead.setValue('');
    }

    public deleteRelatedStrategy(project: ProjectInfoModel): void {
        // remove from UI
        this.relatedStrategies = this.relatedStrategies.filter(p => p.projectId !== project.projectId);

        // remove from Project
        const indexToRemove = this.projectEditForm?.controls.relations.value.findIndex(r => r.relatedProjectId === project.projectId);
        this.projectEditForm?.controls.relations.value.splice(indexToRemove, 1);
        this.projectEditForm?.controls.relations.markAsDirty();
    }

    // #endregion Related Strategies

    // #region Related Projects

    public isRelatedProjectDisabled(project: ProjectInfoModel): boolean {        
        const existingProjectIds = this.projectEditForm?.controls.relations.value.map((p) => p.relatedProjectId);
        return existingProjectIds.includes(project.projectId) || this.isRelationDisabledDueToStatus(project);
    }

    public addRelatedProject(e: MatAutocompleteSelectedEvent) {
        const project = e.option.value as ProjectInfoModel;

        const newRelation = {
            projectId: this.project.projectId,
            relatedProjectId: project.projectId,            
        };

        this.relatedProjects.push(project);
        this.projectEditForm?.controls.relations.value.push(newRelation);

        this.projectEditForm?.controls.relations.markAsDirty();
        this.projectTypeahead.setValue('');
    }

    public deleteRelatedProject(project: ProjectInfoModel): void {
        // remove from UI
        this.relatedProjects = this.relatedProjects.filter(p => p.projectId !== project.projectId);

        // remove from Project
        const indexToRemove = this.projectEditForm?.controls.relations.value.findIndex(r => r.relatedProjectId === project.projectId);
        this.projectEditForm?.controls.relations.value.splice(indexToRemove, 1);
        this.projectEditForm?.controls.relations.markAsDirty();
    }

    // #endregion Related Projects


    // #region Related Whole Systems

    public isWholeSystemDisabled(project: ProjectInfoModel): boolean {        
        const existingProjectIds = this.projectEditForm?.controls.relations.value.map((p) => p.relatedProjectId);
        return existingProjectIds.includes(project.projectId) || this.isRelationDisabledDueToStatus(project);
    }

    public addWholeSystem(e: MatAutocompleteSelectedEvent): void {
        const project = e.option.value as ProjectInfoModel;

        const newRelation = {
            projectId: this.project.projectId,
            relatedProjectId: project.projectId,            
        };

        this.relatedWholeSystems.push(project);
        this.projectEditForm?.controls.relations.value.push(newRelation);

        this.projectEditForm?.controls.relations.markAsDirty();
        this.wholeSystemTypeahead.setValue('');
    }

    public deleteWholeSystem(project: ProjectInfoModel): void {
        // remove from UI
        this.relatedWholeSystems = this.relatedWholeSystems.filter(p => p.projectId !== project.projectId);

        // remove from Project
        const indexToRemove = this.projectEditForm?.controls.relations.value.findIndex(r => r.relatedProjectId === project.projectId);
        this.projectEditForm?.controls.relations.value.splice(indexToRemove, 1);
        this.projectEditForm?.controls.relations.markAsDirty();
    }



    // #endregion Related Whole Systems

    protected isRelationDisabledDueToStatus(option: ProjectInfoModel): boolean {
        const unacceptableStatuses: string[] = ['Completed', 'Canceled'];
        return unacceptableStatuses.includes(option.statusName!);
    }

}
